import produce from 'immer';
import * as _ from 'lodash';

import type { TeamsStore } from '.';
import type {
  AddTeamReleaseGroupsResponse,
  RemoveTeamReleaseGroupsResponse,
  UpdateTeamProjectsResponse,
  UpdateTeamUsersResponse,
} from '../../../typings/api';
import { errorMessagesListFromResponse } from '../../helpers/errors';
import { createReducer, getErrorStatus, getInitialStatus, getPendingStatus } from '../utils';
import {
  ADD_TEAMS_ERROR,
  ADD_TEAM_PROJECTS,
  ADD_TEAM_RELEASE_GROUPS,
  ADD_TEAM_USERS,
  CLEAR_TEAMS_ERRORS,
  CREATE_TEAM,
  DELETE_TEAM,
  FETCH_ALL_TEAMS,
  FETCH_ONE_TEAM,
  REMOVE_TEAM_PROJECTS,
  REMOVE_TEAM_RELEASE_GROUPS,
  REMOVE_TEAM_USERS,
  SET_TEAM_SELECTION,
  SET_TEAM_USER_ROLE,
  UPDATE_TEAM,
} from './actions';

const initialState: TeamsStore = {
  teams: {},
  status: getInitialStatus(),
  teamUpdatingStatus: {
    loading: false,
    errors: [],
  },
  pendingRemovedProjects: [],
  pendingRemovedUsers: [],
  pendingRemovedReleaseGroups: [],
  teamSelection: [],
};

const excludePendingRemovedUsers = (userIds: number[], toExclude: Array<{ id: number }>) =>
  _.differenceBy(userIds, toExclude, user => _.isNumber(user) ? user : user.id);

const excludePendingRemovedProjects = (projectIds: string[], toExcludeIds: string[]) =>
  _.difference(projectIds, toExcludeIds);

const excludePendingRemovedReleaseGroups = (groupIds: number[], toExcludeIds: number[]) =>
  _.difference(groupIds, toExcludeIds);

export default createReducer(initialState, {
  // fetch one actions
  [`${FETCH_ONE_TEAM}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${FETCH_ONE_TEAM}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    draftState.teams[action.meta.id] = action.payload.data;
  }),
  [`${FETCH_ONE_TEAM}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),

  // fetch all actions
  [`${FETCH_ALL_TEAMS}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${FETCH_ALL_TEAMS}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    (action.payload.data || []).forEach((team) => {
      if (team?.id) {
        draftState.teams[team.id] = team;
      }
    });
  }),
  [`${FETCH_ALL_TEAMS}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),

  // create actions
  [`${CREATE_TEAM}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${CREATE_TEAM}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    draftState.teams[action.payload.data.id] = action.payload.data;
  }),
  [`${CREATE_TEAM}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),

  // update actions
  [`${UPDATE_TEAM}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${UPDATE_TEAM}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    const current = draftState.teams[action.meta.id];
    draftState.teams[action.meta.id] = { ...current, ...action.payload.data };
  }),
  [`${UPDATE_TEAM}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),

  // delete actions
  [`${DELETE_TEAM}_PENDING`]: (state) => produce(state, draftState => {
    draftState.status = getPendingStatus();
  }),
  [`${DELETE_TEAM}_FULFILLED`]: (state, action) => produce(state, draftState => {
    draftState.status = getInitialStatus();
    delete draftState.teams[action.meta.id];
  }),
  [`${DELETE_TEAM}_REJECTED`]: (state, action) => produce(state, draftState => {
    draftState.status = getErrorStatus(action.payload);
  }),
  [`${ADD_TEAM_PROJECTS}_PENDING`]: (state) =>
    produce(state, draftState => {
      draftState.teamUpdatingStatus.loading = true;
      draftState.teamUpdatingStatus.errors = [];
    }),
  [`${ADD_TEAM_PROJECTS}_FULFILLED`]: (state, action) =>
    produce(state, draftState => {
      const { id, projects }: UpdateTeamProjectsResponse = action.payload.data;
      const team = draftState.teams[id];
      if (team) {
        team.teamProjects = projects.map(projectId => ({ projectId }));
      }
      draftState.teamUpdatingStatus.loading = false;
    }),
  [`${ADD_TEAM_PROJECTS}_REJECTED`]: (state, action) =>
    produce(state, draftState => {
      draftState.teamUpdatingStatus.loading = false;
      draftState.teamUpdatingStatus.errors = errorMessagesListFromResponse(action.payload);
    }),

  [`${REMOVE_TEAM_PROJECTS}_PENDING`]: (state, action) =>
    produce(state, draftState => {
      draftState.pendingRemovedProjects.push(...action.meta.projectIds);
      draftState.teamUpdatingStatus.loading = true;
      draftState.teamUpdatingStatus.errors = [];
    }),
  [`${REMOVE_TEAM_PROJECTS}_FULFILLED`]: (state, action) =>
    produce(state, draftState => {
      const { id, projects }: UpdateTeamProjectsResponse = action.payload.data;
      const team = draftState.teams[id];
      if (team) {
        team.teamProjects = projects.map(projectId => ({ teamId: id, projectId }));
      }
      draftState.pendingRemovedProjects = excludePendingRemovedProjects(draftState.pendingRemovedProjects, action.meta.projectIds);
      draftState.teamUpdatingStatus.loading = false;
    }),
  [`${REMOVE_TEAM_PROJECTS}_REJECTED`]: (state, action) =>
    produce(state, draftState => {
      draftState.pendingRemovedProjects = excludePendingRemovedProjects(draftState.pendingRemovedProjects, action.meta.projectIds);
      draftState.teamUpdatingStatus.loading = false;
      draftState.teamUpdatingStatus.errors = errorMessagesListFromResponse(action.payload);
    }),

    [`${ADD_TEAM_RELEASE_GROUPS}_PENDING`]: (state) =>
    produce(state, draftState => {
      draftState.teamUpdatingStatus.loading = true;
      draftState.teamUpdatingStatus.errors = [];
    }),
  [`${ADD_TEAM_RELEASE_GROUPS}_FULFILLED`]: (state, action) =>
    produce(state, draftState => {
      const { id, releaseGroups }: AddTeamReleaseGroupsResponse = action.payload.data;
      const team = draftState.teams[id];
      if (team) {
        team.teamReleaseGroups = releaseGroups.map(releaseGroupId => ({ releaseGroupId }));
      }
      draftState.teamUpdatingStatus.loading = false;
    }),
  [`${ADD_TEAM_RELEASE_GROUPS}_REJECTED`]: (state, action) =>
    produce(state, draftState => {
      draftState.teamUpdatingStatus.loading = false;
      draftState.teamUpdatingStatus.errors = errorMessagesListFromResponse(action.payload);
    }),

  [`${REMOVE_TEAM_RELEASE_GROUPS}_PENDING`]: (state, action) =>
    produce(state, draftState => {
      draftState.pendingRemovedReleaseGroups.push(...action.meta.groupIds);
      draftState.teamUpdatingStatus.loading = true;
      draftState.teamUpdatingStatus.errors = [];
    }),
  [`${REMOVE_TEAM_RELEASE_GROUPS}_FULFILLED`]: (state, action) =>
    produce(state, draftState => {
      const { id, releaseGroups }: RemoveTeamReleaseGroupsResponse = action.payload.data;
      const team = draftState.teams[id];
      if (team) {
        team.teamReleaseGroups = releaseGroups.map(releaseGroupId => ({ teamId: id, releaseGroupId }));
      }
      draftState.pendingRemovedReleaseGroups = excludePendingRemovedReleaseGroups(draftState.pendingRemovedReleaseGroups, action.meta.groupIds);
      draftState.teamUpdatingStatus.loading = false;
    }),
  [`${REMOVE_TEAM_RELEASE_GROUPS}_REJECTED`]: (state, action) =>
    produce(state, draftState => {
      draftState.pendingRemovedReleaseGroups = excludePendingRemovedReleaseGroups(draftState.pendingRemovedReleaseGroups, action.meta.groupIds);
      draftState.teamUpdatingStatus.loading = false;
      draftState.teamUpdatingStatus.errors = errorMessagesListFromResponse(action.payload);
    }),

  [`${ADD_TEAM_USERS}_PENDING`]: (state) =>
    produce(state, draftState => {
      draftState.teamUpdatingStatus.loading = true;
      draftState.teamUpdatingStatus.errors = [];
    }),
  [`${ADD_TEAM_USERS}_FULFILLED`]: (state, action) =>
    produce(state, draftState => {
      const { id, users }: UpdateTeamUsersResponse = action.payload.data;
      const team = draftState.teams[id];
      if (team) {
        team.teamUsers = users;
      }
      draftState.teamUpdatingStatus.loading = false;
    }),
  [`${ADD_TEAM_USERS}_REJECTED`]: (state, action) =>
    produce(state, draftState => {
      draftState.teamUpdatingStatus.loading = false;
      draftState.teamUpdatingStatus.errors = errorMessagesListFromResponse(action.payload);
    }),

  [`${REMOVE_TEAM_USERS}_PENDING`]: (state, action) =>
    produce(state, draftState => {
      draftState.pendingRemovedUsers.push(...action.meta.users.map(user => user.id));
      draftState.teamUpdatingStatus.loading = true;
      draftState.teamUpdatingStatus.errors = [];
    }),
  [`${REMOVE_TEAM_USERS}_FULFILLED`]: (state, action) =>
    produce(state, draftState => {
      draftState.pendingRemovedUsers = excludePendingRemovedUsers(draftState.pendingRemovedUsers, action.meta.users);
      const { id, users }: UpdateTeamUsersResponse = action.payload.data;
      const team = draftState.teams[id];
      if (team) {
        team.teamUsers = users;
      }
      draftState.teamUpdatingStatus.loading = false;
    }),
  [`${REMOVE_TEAM_USERS}_REJECTED`]: (state, action) =>
    produce(state, draftState => {
      draftState.pendingRemovedUsers = excludePendingRemovedUsers(draftState.pendingRemovedUsers, action.meta.users);
      draftState.teamUpdatingStatus.loading = false;
      draftState.teamUpdatingStatus.errors = errorMessagesListFromResponse(action.payload);
    }),

  [SET_TEAM_USER_ROLE]: (state, action) =>
    produce(state, draftState => {
      const { teamId, ...userRole } = action.payload;
      if (draftState.teams[teamId]) {
        draftState.teams[teamId].teamUsers = _.unionBy(
          [userRole],
          draftState.teams[teamId].teamUsers || [],
          teamUser => teamUser.userId,
        );
      }
    }),

  [ADD_TEAMS_ERROR]: (state, action) =>
    produce(state, draftState => {
      draftState.status.errors.push(action.payload);
    }),

  [CLEAR_TEAMS_ERRORS]: (state) => produce(state, draftState => {
    draftState.status.errors = [];
    draftState.teamUpdatingStatus.errors = [];
  }),

  [SET_TEAM_SELECTION]: (state, action) =>
    produce(state, draftState => {
      draftState.teamSelection = action.payload;
    }),
});
